import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { HttpHandler } from '@angular/common/http';
import { HttpInterceptor } from '@angular/common/http';

import { Observable } from 'rxjs';

import { SessionService } from '../service/auth/session.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {

  constructor(private sessionService: SessionService) {
  }

  intercept(request: HttpRequest<any>,
            next: HttpHandler): Observable<HttpEvent<any>> {

    if (!this.isLogin(request) &&
        this.isOrdyx(request) &&
        this.sessionService
            .isValid()) {

      // Every authenticated call now goes through the BFF — the old
      // POS-direct branch (boh-api with a tokenPos bearer) is gone, along
      // with its apiURLPOS/apiURL substring-collision hazard.
      if (this.isBff(request)) {
        // BFF: send SESSION cookie, no manual auth headers
        request = request.clone({withCredentials: true});

        // Partner comes from THIS tab's selection, like pre-BFF. The BFF
        // session is shared by every tab in the browser, so relying on the
        // session's last-switched partner let tab A silently act on tab B's
        // tenant. The header is a tenancy selector, not a credential —
        // portal-api authorizes it on every request; the BFF falls back to
        // the session's partner only when no header is sent.
        const partner = this.sessionService
                            .getPartner();

        if (partner?.id !== undefined &&
            !request.headers
                    .has('Partner')) {

          request = request.clone({setHeaders: {Partner: partner.id
                                                                .toString()}});
        }
      }

      request = this.versioned(request);
    } else if (this.isBff(request)) {
      // Login/public BFF endpoints also need withCredentials for cookie
      request = request.clone({withCredentials: true});
    }

    // CSRF: copy the BFF-issued XSRF-TOKEN cookie value into the X-XSRF-TOKEN
    // header on state-changing BFF requests (logout DELETE included). The
    // BFF's CsrfWebFilter validates these match (double-submit cookie
    // pattern). Login endpoints are exempt on the BFF side, but we send the
    // header anyway when the cookie exists — the BFF ignores it on exempt
    // paths and the interceptor stays simple. POS-direct requests keep their
    // Bearer token and are never CSRF-checked.
    request = this.attachCsrfHeader(request);

    console.log(request.url);

    return next.handle(request);
  }

  private attachCsrfHeader(request: HttpRequest<any>): HttpRequest<any> {
    const stateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'];

    if (!this.isBff(request)) {
      return request;
    }

    if (!stateChanging.includes(request.method.toUpperCase())) {
      return request;
    }

    if (request.headers.has('X-XSRF-TOKEN')) {
      return request;
    }

    const token = this.readCookie('XSRF-TOKEN');

    if (!token) {
      return request;
    }

    return request.clone({ setHeaders: { 'X-XSRF-TOKEN': token } });
  }

  private readCookie(name: string): string | null {
    const match = document.cookie
      .split('; ')
      .find(c => c.startsWith(`${name}=`));

    return match ? decodeURIComponent(match.split('=')[1]) : null;
  }

  private isLogin(request: HttpRequest<object>): boolean {
    let is = false;

    if (request.method === 'POST' &&
        request.url.includes('login')){
      is = true;
    }

    return is;
  }

  private isOrdyx(request: HttpRequest<object>): boolean {
    let is = false;

    if (request.url?.includes('ordyx.com') ||
        request.url?.includes('tonicpos.com') ||
        request.url?.includes('localhost')) {
      is = true;
    }

    return is;
  }

  private isBff(request: HttpRequest<object>): boolean {
    // Guard the empty string: url.includes('') is always true, which would
    // mark every request as BFF-bound in environments where apiURLAuth is
    // not yet configured.
    return (!!environment.apiURLAuth &&
            request.url?.includes(environment.apiURLAuth)) ||
           request.url?.includes('/bff/');
  }

  private versioned(req: HttpRequest<object>): HttpRequest<any>{
    let cloned: HttpRequest<any> = req;
    let version = this.sessionService.version;

    if (version !== undefined &&
        version != null) {
      // Add the version to all requests to make sure the client always has the latest code
      cloned = cloned.clone({setParams: {v: version}});
    }

    return cloned;
  }
}
